import { ClockIcon, CubeIcon } from '@heroicons/react/24/outline';
import {
  type FilterDefinition,
  type FilterGeneratorContext,
  type FilterSuggestion,
  type SerializedFilter,
  type ActiveFilter
} from '@oysterjs/ui/search-filter';
import { client } from '~/api/client';
import {
  GetCustomPresetsDocument,
  GetCustomPresetsQuery,
  GetCustomPresetsQueryVariables,
  CaraWorkspaceState,
  CaraWorkspacePreset,
  Visibility,
  type SearchCaraWorkspacesFilterInput
} from '~/api/graphql';
import {
  BaseDateFilter,
  BaseSearchFilter,
  BaseSharedWithMeFilter,
  BaseTeamFilter,
  BaseUserFilter,
  BaseVisibilityFilter
} from '~/api/data/filter-base';

export type WorkspaceFilter =
  | 'status'
  | 'user'
  | 'preset'
  | 'date'
  | 'search'
  | 'visibility'
  | 'sharedWithMe'
  | 'team';

const titleCase = (s: string) =>
  s
    .split(/[\s_]/g)
    .map((part) => part[0]?.toUpperCase() + part.substring(1).toLowerCase())
    .join(' ');

const getWorkspaceStateTitle = (state: CaraWorkspaceState) => {
  switch (state) {
    case CaraWorkspaceState.Created:
      return 'Created';
    case CaraWorkspaceState.Idle:
      return 'Idle';
    case CaraWorkspaceState.WaitingForInput:
      return 'Waiting';
    case CaraWorkspaceState.Working:
      return 'Working';
  }
};

export { getWorkspaceStateTitle };

/** Map active filters to server-side SearchCaraWorkspacesFilterInput */
export function activeFiltersToSearchFilter(
  filters: ActiveFilter<WorkspaceFilter>[]
): SearchCaraWorkspacesFilterInput | undefined {
  if (filters.length === 0) return undefined;
  const out: SearchCaraWorkspacesFilterInput = {};
  for (const f of filters) {
    switch (f.type) {
      case 'search':
        out.search = f.value as string;
        break;
      case 'status':
        out.state = f.value as CaraWorkspaceState;
        break;
      case 'user':
        out.agencyUserId = f.value as string;
        break;
      case 'date': {
        const startOfDay = new Date(new Date().setHours(0, 0, 0, 0));
        const ms = (days: number) => days * 24 * 60 * 60 * 1000;
        let since: Date;
        switch (f.value) {
          case 'today':
            since = startOfDay;
            break;
          case 'since yesterday':
            since = new Date(startOfDay.getTime() - ms(1));
            break;
          case 'since last week':
            since = new Date(startOfDay.getTime() - ms(7));
            break;
          case 'since last month':
            since = new Date(startOfDay.getTime() - ms(30));
            break;
          case 'since last year':
            since = new Date(startOfDay.getTime() - ms(365));
            break;
          default:
            continue;
        }
        out.updatedAfter = since;
        break;
      }
      case 'preset': {
        const v = f.value as string;
        if (Object.values(CaraWorkspacePreset).includes(v as CaraWorkspacePreset)) {
          out.preset = v as CaraWorkspacePreset;
        } else {
          out.customPresetId = v;
        }
        break;
      }
      case 'visibility':
        if (Object.values(Visibility).includes(f.value as Visibility)) {
          out.visibilityLevel = f.value as Visibility;
        }
        break;
      case 'sharedWithMe':
        out.sharedWithMe = true;
        break;
      case 'team':
        out.sharedWithTeamId = f.value as string;
        break;
      default:
        break;
    }
  }
  return Object.keys(out).length === 0 ? undefined : out;
}

export class StatusFilter implements FilterDefinition<WorkspaceFilter> {
  readonly type = 'status' as const;
  readonly icon = ClockIcon;
  readonly priority = 0;
  readonly isUnique = false;

  generate({ query }: FilterGeneratorContext<WorkspaceFilter>): FilterSuggestion[] {
    return Object.values(CaraWorkspaceState)
      .filter(
        (s) =>
          getWorkspaceStateTitle(s).toLowerCase().includes(query.toLowerCase()) ||
          'status'.includes(query.toLowerCase())
      )
      .map((s) => ({
        value: s,
        displayLabel: 'Status is',
        displayValue: getWorkspaceStateTitle(s)
      }));
  }

  deserialize(s: SerializedFilter<WorkspaceFilter>): ActiveFilter<WorkspaceFilter> | null {
    if (!Object.values(CaraWorkspaceState).includes(s.value as CaraWorkspaceState)) {
      return null;
    }
    return {
      id: `status-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: 'Status is',
      displayValue: s.displayValue
    };
  }
}

export class UserFilter extends BaseUserFilter<WorkspaceFilter> {
  constructor() {
    super('user');
  }
}

export class PresetFilter implements FilterDefinition<WorkspaceFilter> {
  readonly type = 'preset' as const;
  readonly icon = CubeIcon;
  readonly priority = 20;
  readonly isUnique = false;
  private cachedCustomPresets: Array<{ id: string; title: string }> | null = null;

  async generate({ query }: FilterGeneratorContext<WorkspaceFilter>): Promise<FilterSuggestion[]> {
    if (this.cachedCustomPresets === null) {
      try {
        const data = await client.query<GetCustomPresetsQuery, GetCustomPresetsQueryVariables>({
          query: GetCustomPresetsDocument,
          variables: {}
        });
        const presets = data?.data?.customPresets ?? [];
        this.cachedCustomPresets = presets.map((p) => ({ id: p.id, title: p.title }));
      } catch {
        this.cachedCustomPresets = [];
      }
    }

    const options: { value: string; name: string }[] = [
      ...Object.values(CaraWorkspacePreset).map((p) => ({ value: p, name: titleCase(p) })),
      ...this.cachedCustomPresets.map((p) => ({ value: p.id, name: p.title }))
    ];
    return options
      .filter(
        (o) =>
          o.name.toLowerCase().includes(query.toLowerCase()) ||
          'preset'.includes(query.toLowerCase())
      )
      .map((o) => ({
        value: o.value,
        displayLabel: 'Preset is',
        displayValue: o.name
      }));
  }

  deserialize(s: SerializedFilter<WorkspaceFilter>): ActiveFilter<WorkspaceFilter> {
    return {
      id: `preset-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: 'Preset is',
      displayValue: s.displayValue
    };
  }
}

export class DateFilter extends BaseDateFilter<WorkspaceFilter> {
  constructor() {
    super('date');
  }
}

export class SearchFilter extends BaseSearchFilter<WorkspaceFilter> {
  constructor() {
    super('search');
  }
}

export class VisibilityFilter extends BaseVisibilityFilter<WorkspaceFilter> {
  constructor() {
    super('visibility');
  }
}

export class SharedWithMeFilter extends BaseSharedWithMeFilter<WorkspaceFilter> {
  constructor() {
    super('sharedWithMe');
  }
}

export class TeamFilter extends BaseTeamFilter<WorkspaceFilter> {
  constructor() {
    super('team');
  }
}

export const createFilterDefinitions = (): FilterDefinition<WorkspaceFilter>[] => [
  new StatusFilter(),
  new UserFilter(),
  new PresetFilter(),
  new DateFilter(),
  new SearchFilter(),
  new VisibilityFilter(),
  new SharedWithMeFilter(),
  new TeamFilter()
];
